<template>
  <section v-if="addons && addons.length" class="phd-addons">
    <header class="phd-addons-head">
      <h3 class="phd-addons-title">Add-ons</h3>
      <p class="phd-addons-sub">Optional extras — toggle anything that fits your team.</p>
    </header>

    <ul class="phd-addons-list">
      <li
        v-for="addon in addons"
        :key="addon.key"
        class="phd-addon-row"
        :class="{ 'phd-addon-row--on': isSelected(addon.key) }"
      >
        <label class="phd-addon-label">
          <span class="phd-addon-check-wrap">
            <input
              type="checkbox"
              class="phd-addon-checkbox"
              :checked="isSelected(addon.key)"
              @change="toggle(addon.key)"
            />
            <span class="phd-addon-check" aria-hidden="true">
              <span v-if="isSelected(addon.key)">✓</span>
            </span>
          </span>
          <span class="phd-addon-body">
            <span class="phd-addon-name">{{ addon.name }}</span>
            <span class="phd-addon-desc">{{ descriptionFor(addon.key) }}</span>
          </span>
          <span class="phd-addon-price">
            <span class="phd-addon-amount">${{ formatPrice(addon.price) }}</span>
            <span class="phd-addon-unit">{{ addon.unit }}</span>
          </span>
        </label>
      </li>
    </ul>
  </section>
</template>

<script>
// Inline copy for each addon — backend doesn't ship descriptions yet,
// so we keep them keyed here. Falls back to a generic line for any
// future addon key the backend introduces. Lifted from the legacy
// /sign-up AddOns.vue addons_config descriptions so a buyer who's
// seen both flows reads the same one-liner.
const ADDON_DESCRIPTIONS = {
  food: 'Required food-handler certification for restaurant teams.',
  sexual: 'State-compliant sexual harassment prevention training.',
  human: 'Hospitality-industry human trafficking awareness.',
  hours_safety: 'Essential workplace safety to reduce injury risk.',
  sms_support: 'Course + company SMS notifications to your team.',
  surveys: 'Anonymous post-training feedback to improve programs.',
  upload_training: 'Upload your own SCORM courses to assign and track.',
  on_boarding: 'White-glove team upload + dashboard configuration.',
  call_location: 'Quarterly account-management calls per location.',
};

export default {
  name: 'AddOnsPicker',

  props: {
    // Raw addons array as returned by /new-checkout/pricing-bootstrap.
    addons: { type: Array, required: true },
    // Currently selected addon keys (v-model'd from parent).
    value: { type: Array, default: () => [] },
    // Kept for future use — parent may want to drive a per-addon monthly
    // estimate. Current design hides the estimate to reduce visual
    // noise; the cart sidebar surfaces the canonical total.
    users: { type: Number, default: 0 },
    locations: { type: Number, default: 0 },
  },

  computed: {
    selectedKeys() {
      return Array.isArray(this.value) ? this.value : [];
    },
  },

  methods: {
    formatPrice(n) {
      const num = Number(n);
      if (!Number.isFinite(num)) return '0.00';
      return num.toFixed(2);
    },
    descriptionFor(key) {
      return ADDON_DESCRIPTIONS[key] || 'Optional add-on for your plan.';
    },
    isSelected(key) {
      return this.selectedKeys.indexOf(key) !== -1;
    },
    toggle(key) {
      const next = this.selectedKeys.slice();
      const idx = next.indexOf(key);
      if (idx === -1) next.push(key);
      else next.splice(idx, 1);
      this.$emit('input', next);
    },
  },
};
</script>

<style lang="scss" scoped>
/* Single-column horizontal rows — same rhythm legacy /sign-up's
   AddOns.vue used (checkbox left, content middle, price right) but
   themed to the v3 design tokens (HOT orange #fe672c, 12px radius,
   Argon-style shadows on hover/active). */

.phd-addons {
  margin-top: 28px;
}

.phd-addons-head {
  margin-bottom: 14px;
  h3.phd-addons-title {
    font-size: 18px;
    line-height: 22px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 4px;
  }
  .phd-addons-sub {
    font-size: 13px;
    line-height: 18px;
    color: #666;
    margin: 0;
  }
}

.phd-addons-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.phd-addon-row {
  background: #fff;
  border: 1px solid rgba(37, 37, 37, 0.1);
  border-radius: 12px;
  transition: border-color 180ms ease, background-color 180ms ease,
              box-shadow 180ms ease, transform 120ms ease;

  &:hover {
    border-color: rgba(254, 103, 44, 0.4);
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.04);
  }

  &--on {
    border-color: #fe672c;
    background: linear-gradient(135deg, #fff8f4 0%, #fff 100%);
    box-shadow: 0 6px 16px rgba(254, 103, 44, 0.10);
  }
}

.phd-addon-label {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  margin: 0;
  cursor: pointer;
}

.phd-addon-check-wrap {
  position: relative;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.phd-addon-checkbox {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.phd-addon-check {
  position: absolute;
  inset: 0;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid rgba(200, 200, 200, 0.7);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 12px;
  line-height: 1;
  transition: background-color 160ms ease, border-color 160ms ease;
  pointer-events: none;

  .phd-addon-row--on & {
    background: #fe672c;
    border-color: #fe672c;
  }
}

.phd-addon-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.phd-addon-name {
  font-size: 15px;
  line-height: 20px;
  font-weight: 700;
  color: #1a1a1a;
}

.phd-addon-desc {
  font-size: 13px;
  line-height: 17px;
  color: #666;
  margin-top: 2px;
}

.phd-addon-price {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  flex-shrink: 0;
  white-space: nowrap;
}

.phd-addon-amount {
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  color: #1a1a1a;
}

.phd-addon-unit {
  font-size: 12px;
  line-height: 15px;
  color: #777;
  margin-top: 2px;
}

@media (max-width: 480px) {
  .phd-addon-label {
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    gap: 8px 14px;
  }
  .phd-addon-price {
    grid-column: 2 / 3;
    grid-row: 2 / 3;
    flex-direction: row;
    align-items: baseline;
    gap: 6px;
    text-align: left;
  }
  .phd-addon-amount { font-size: 14px; }
  .phd-addon-unit { margin-top: 0; }
}
</style>
